import { useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import { tokenRefreshService } from "@/services/utils/tokenRefreshService";
import { isTokenExpired as checkTokenExpired } from "@/utils/jwtUtils";
import useAuthStore from "@/stores/auth";

const MAX_REFRESH_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

export const useTokenRefresh = () => {
  const { clearAuth } = useAuthStore();
  const retryCountRef = useRef(0);

  const refreshTokens = useCallback(async (): Promise<string> => {
    try {
      const refreshToken = tokenRefreshService.getStoredRefreshToken();

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const newAccessToken = await tokenRefreshService.refreshToken(
        refreshToken
      );

      // Reset retry count on success
      retryCountRef.current = 0;

      // Update the auth store with the new token
      // Note: The tokenRefreshService already updates localStorage
      // We just need to trigger a store update if needed
      return newAccessToken;
    } catch (error) {
      console.error("Token refresh failed:", error);

      // Increment retry count
      retryCountRef.current += 1;

      if (retryCountRef.current >= MAX_REFRESH_RETRIES) {
        // Permanently failed — force logout immediately
        console.error(`Token refresh failed after ${MAX_REFRESH_RETRIES} attempts, logging out`);
        retryCountRef.current = 0;
        clearAuth();
        toast.error("Session expired. Please login again.");

        // Redirect to login
        if (
          !window.location.pathname.includes("/login") &&
          window.location.pathname !== "/"
        ) {
          window.location.href = "/login";
        }
      } else {
        console.log(`Token refresh attempt ${retryCountRef.current} failed, will retry...`);
        // Wait briefly then retry once more before giving up
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        // Attempt the retry immediately
        try {
          const retryRefreshToken = tokenRefreshService.getStoredRefreshToken();
          if (retryRefreshToken) {
            const result = await tokenRefreshService.refreshToken(retryRefreshToken);
            retryCountRef.current = 0;
            return result;
          }
        } catch {
          // Retry also failed — force logout now
          console.error("Token refresh retry failed, logging out");
          retryCountRef.current = 0;
          clearAuth();
          toast.error("Session expired. Please login again.");
          if (
            !window.location.pathname.includes("/login") &&
            window.location.pathname !== "/"
          ) {
            window.location.href = "/login";
          }
        }
      }

      throw error;
    }
  }, [clearAuth]);

  const isTokenExpired = useCallback(() => {
    try {
      const token = tokenRefreshService.getStoredAccessToken();
      if (!token) return true;

      return checkTokenExpired(token);
    } catch (error) {
      console.error("Error checking token expiry:", error);
      return true;
    }
  }, []);

  return {
    refreshTokens,
    isTokenExpired,
  };
};
